import React, { useEffect, useContext, useState } from 'react';
import { Container, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import '../styles/css/FAQ.css';
// Import animation components
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { LanguageContext } from '../App';

const FAQ = () => {
  const [expandedId, setExpandedId] = useState(null);

  const handleAccordionChange = (id) => (event, isExpanded) => {
    setExpandedId(isExpanded ? id : null);
  };

  // Add animation controls
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // Trigger animations when section becomes visible
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100
      }
    }
  };

  const { language, translations } = useContext(LanguageContext);

  const faqItems = translations[language].faqItems;

  return (
    <Container className="faq-container">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="section-title">
          {translations[language].faqTitle}
        </h2>
        <p className="section-subtitle">
          {translations[language].faqSubtitle1}<br/>
          {translations[language].faqSubtitle2}
        </p>        
      </motion.div>
      
      <motion.div
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={containerVariants}
        className="faq-accordion-container"
      >
        {faqItems.map((item, index) => (
          <motion.div key={item.id} variants={itemVariants}>
            <Accordion 
              className="faq-accordion"
              component={motion.div}
              expanded={expandedId === item.id}
              onChange={handleAccordionChange(item.id)}
              whileHover={{ scale: 1.01, boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)" }}
            >
              <AccordionSummary
                expandIcon={<span className="faq-plusminus" aria-hidden="true" />}
                aria-controls={`panel${item.id}-content`}
                id={`panel${item.id}-header`}
                className="faq-accordion-summary"
              >
                <h3 className="faq-question">
                  {item.question}
                </h3>
              </AccordionSummary>
              <AccordionDetails className="faq-accordion-details">
                <motion.div 
                  className="faq-answer"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  dangerouslySetInnerHTML={{ __html: item.answer }}
                />
              </AccordionDetails>
            </Accordion>
          </motion.div>
        ))}
      </motion.div>
      
      <motion.div 
        className="faq-more-questions text-center" 
        style={{ marginTop: "2rem" }}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.5 }}
      >
        <h3 className="faq-more-title">
          {translations[language].faqMoreQuestionsTitle}
        </h3>
        <p className="faq-more-body">
          {translations[language].faqMoreQuestionsBody}
        </p>
      </motion.div>
    </Container>
  );
}

export default FAQ;